import { useNavigate } from "react-router-dom";
import { LifeBuoy, ChevronRight, Inbox } from "lucide-react";
import { TICKET_STATUS, TICKET_PRIORITY, SLA_BADGE } from "@/constants/tickets";

const Chip = ({ cls, children }) => <span className={`text-[11px] px-2 py-0.5 rounded-sm font-medium whitespace-nowrap ${cls}`}>{children}</span>;

function TicketRow({ t, who, onOpen }) {
  const sla = SLA_BADGE[t.sla_state];
  return (
    <button onClick={() => onOpen(t.id)} data-testid={`my-ticket-${t.id}`}
      className="w-full flex items-center gap-3 border border-border bg-card rounded-sm px-3 py-2.5 hover:border-primary/60 transition-colors text-left">
      <span className={`h-2 w-2 rounded-full shrink-0 ${TICKET_PRIORITY[t.priority]?.dot || "bg-muted-foreground"}`} />
      <span className="font-mono text-xs text-muted-foreground shrink-0">{t.number}</span>
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-medium truncate">{t.subject}</span>
        <span className="block text-xs text-muted-foreground truncate">{t.group_name}{who ? ` · ${who}` : ""}</span>
      </span>
      {sla && t.sla_state !== "ok" && <Chip cls={sla.cls}>{sla.label}</Chip>}
      <Chip cls={TICKET_STATUS[t.status]?.cls || "bg-muted"}>{TICKET_STATUS[t.status]?.label || t.status}</Chip>
      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
    </button>
  );
}

export function MyTicketsPanel({ work }) {
  const navigate = useNavigate();
  const open = (id) => navigate(`/tickets?open=${id}`);
  const total = work.assigned.length + work.requested.length;
  return (
    <div data-testid="my-tickets-panel">
      <div className="flex items-center gap-2 mb-2 text-sm font-semibold">
        <LifeBuoy className="h-4 w-4 text-primary" /> My Tickets <span className="text-muted-foreground font-normal">({total})</span>
        <button onClick={() => navigate("/tickets")} className="ml-auto text-xs text-primary hover:underline" data-testid="my-tickets-open-helpdesk">Open Monge Ticketing →</button>
      </div>
      {total === 0 ? (
        <div className="border border-dashed border-border rounded-sm p-4 text-xs text-muted-foreground flex items-center gap-2" data-testid="my-tickets-empty"><Inbox className="h-4 w-4" /> No open tickets assigned to you or requested by you.</div>
      ) : (
        <div className="space-y-4">
          {work.assigned.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">Assigned to me ({work.assigned.length})</div>
              <div className="space-y-2">{work.assigned.map((t) => <TicketRow key={t.id} t={t} who={t.requester_name ? `from ${t.requester_name}` : ""} onOpen={open} />)}</div>
            </div>
          )}
          {work.requested.length > 0 && (
            <div>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1.5">My requests ({work.requested.length})</div>
              <div className="space-y-2">{work.requested.map((t) => <TicketRow key={t.id} t={t} who={t.assignee_name ? `with ${t.assignee_name}` : "unassigned"} onOpen={open} />)}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
